// src/pages/AdminDragBuilder.tsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { nanoid } from "nanoid";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

type FieldType = "text" | "email" | "number" | "textarea" | "select" | "radio" | "checkbox" | "file";

type Field = {
  id: string; // local id
  field_key: string; // unique key persisted to DB
  label: string;
  type: FieldType;
  required?: boolean;
  options?: string[]; // for select/radio
};

const TOOLBOX: { type: FieldType; label: string }[] = [
  { type: "text", label: "Text" },
  { type: "email", label: "Email" },
  { type: "number", label: "Number" },
  { type: "textarea", label: "Textarea" },
  { type: "select", label: "Select" },
  { type: "radio", label: "Radio" },
  { type: "checkbox", label: "Checkbox" },
  { type: "file", label: "File" },
];

function SortableItem({ item, onChange, onRemove }: { item: Field; onChange: (f: Field) => void; onRemove: (id: string) => void; }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white border p-3 rounded shadow-sm">
      <div className="flex justify-between items-start gap-2">
        <div>
          <div className="text-sm font-medium">{item.label}</div>
          <div className="text-xs text-gray-500">{item.type}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button {...attributes as any} {...listeners as any} className="text-xs px-2 py-1 border rounded">↕</button>
          <button onClick={() => onRemove(item.id)} className="text-xs text-red-600">Remove</button>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        <input value={item.label} onChange={(e)=>onChange({...item, label: e.target.value})} className="w-full p-2 border rounded text-sm" />
        <label className="inline-flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!item.required} onChange={(e)=>onChange({...item, required: e.target.checked})} />
          Required
        </label>
        {(item.type === 'select' || item.type === 'radio') && (
          <div>
            <label className="text-xs text-gray-600">Options (comma separated)</label>
            <input value={(item.options||[]).join(',')} onChange={(e)=>onChange({...item, options: e.target.value.split(',').map(s=>s.trim())})} className="w-full p-2 border rounded text-sm" />
          </div>
        )}
      </div>
    </div>
  );
}

const AdminDragBuilder=() =>{
     const api = process.env.REACT_APP_API_URL as string;
  const [title, setTitle] = useState("New Form");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<Field[]>([]);
  const [slugLink, setSlugLink] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor));
  const [saving, setSaving] = useState(false);

  useEffect(()=> {
    // initialize empty or load existing form if editing
  }, []);

  function addFieldFromToolbox(type: FieldType) {
    const id = nanoid();
    setFields(prev => [...prev, { id, field_key: nanoid(8), label: `${type} field`, type, required: false, options: [] }]);
  }

  function updateField(f: Field) {
    setFields(prev => prev.map(p => p.id === f.id ? f : p));
  }

  function removeField(id:string) {
    setFields(prev => prev.filter(p => p.id !== id));
  }

  function handleDragEnd(event:any) {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = fields.findIndex(f => f.id === active.id);
      const newIndex = fields.findIndex(f => f.id === over.id);
      setFields((items) => arrayMove(items, oldIndex, newIndex));
    }
  }

  async function saveForm() {
    setSaving(true);
    try {
      // prepare payload with fields in DB shape
      const payload = {
        title,
        description,
        fields: fields.map((f, idx) => ({
          field_key: f.field_key,
          label: f.label,
          type: f.type,
          required: f.required,
          options: f.options,
          meta: { placeholder: '' },
          sort_order: idx
        }))
      };
      const res = await axios.post(`${api}/auth/createform`, payload, { withCredentials: true });
      const slug = res.data.form.slug;
      const url = `${window.location.origin}/form/${slug}`;
      setSlugLink(url);
    } catch (err:any) {
      console.error(err);
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function copyLink() {
    if (!slugLink) return;
    await navigator.clipboard.writeText(slugLink);
    alert('Link copied');
  }

  return (
    <div className="min-h-screen p-6 bg-slate-50">
      <div className="max-w-7xl mx-auto grid grid-cols-12 gap-6">
        {/* Left Toolbox */}
        <div className="col-span-3 bg-white p-4 rounded shadow">
          <h3 className="font-semibold mb-3">Toolbox</h3>
          <div className="space-y-2">
            {TOOLBOX.map(t => (
              <button key={t.type} onClick={()=>addFieldFromToolbox(t.type)} className="w-full text-left p-2 border rounded hover:bg-gray-50">{t.label}</button>
            ))}
          </div>
          <div className="mt-6">
            <h4 className="text-sm font-medium mb-1">Form Settings</h4>
            <input value={title} onChange={e=>setTitle(e.target.value)} className="w-full p-2 border rounded mb-2" placeholder="Form title" />
            <textarea value={description} onChange={e=>setDescription(e.target.value)} className="w-full p-2 border rounded" placeholder="Description (optional)" />
          </div>
        </div>

        {/* Center Canvas */}
        <div className="col-span-6">
          <div className="bg-white p-4 rounded shadow">
            <h3 className="font-semibold mb-3">Builder</h3>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {fields.length === 0 && <div className="p-6 text-sm text-gray-500">Add fields from toolbox</div>}
                  {fields.map(f => (
                    <SortableItem key={f.id} item={f} onChange={updateField} onRemove={removeField} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <div className="mt-4 flex gap-2">
              <button onClick={saveForm} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded">Save & Generate Link</button>
              <button onClick={()=>{ setFields([]); setSlugLink(null); }} className="px-4 py-2 border rounded">Reset</button>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="col-span-3">
          <div className="sticky top-6 bg-white p-4 rounded shadow">
            <h3 className="font-semibold mb-2">Share Link & Preview</h3>
            <div className="mb-4">
              <label className="text-xs text-gray-500">Public Link</label>
              <div className="mt-2 flex gap-2">
                <input value={slugLink || ''} readOnly className="flex-1 p-2 border rounded" />
                <button onClick={copyLink} className="px-3 py-2 bg-blue-600 text-white rounded">Copy</button>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Preview</h4>
              <div className="border rounded p-3 bg-gray-50 space-y-2 max-h-64 overflow-auto">
                <h5 className="text-base font-semibold">{title}</h5>
                {description && <div className="text-xs text-gray-600">{description}</div>}
                <div className="mt-2 space-y-2">
                  {fields.map(f => (
                    <div key={f.id} className="p-2 bg-white border rounded">
                      <div className="text-sm font-medium">{f.label}{f.required ? ' *' : ''}</div>
                      <div className="text-xs text-gray-500">{f.type}</div>
                    </div>
                  ))}
                  {fields.length === 0 && <div className="text-xs text-gray-500">No fields yet</div>}
                </div>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-600">
              Responses are accessible on the admin responses page after visitors submit.
            </div>
            <a className="mt-3 inline-block text-blue-600" href="/admin/responses">Responses Page</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDragBuilder;